import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button } from "reactstrap";
import { Catalogue } from "../../types/catalogue.types";
import CatalogueAddFromInventory from "./CatalogueAddFromInventory";
import {
  CATALOGUE_LIST_PATH,
  parseCatalogueAddFromInventoryId,
} from "./cataloguePaths";
import { isSystemManagedCatalogue } from "./catalogueSystemManaged";
import { hasNamedCatalogueSeed, resolveCatalogue } from "./catalogueResolve";
import "./catalogue-manager.scss";

type AddFromInventoryLocationState = {
  catalogue?: Catalogue;
};

const CatalogueAddFromInventoryPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<AddFromInventoryLocationState>();
  const catalogueId =
    parseCatalogueAddFromInventoryId(location.pathname) ||
    location.state?.catalogue?.id ||
    "";
  const seeded = location.state?.catalogue;
  const [catalogue, setCatalogue] = useState<Catalogue | null>(() =>
    hasNamedCatalogueSeed(seeded, catalogueId) ? seeded : null
  );
  const [loading, setLoading] = useState(!catalogue);
  const [error, setError] = useState("");

  const goToCatalogue = (next?: Catalogue) => {
    const open = next || catalogue;
    history.push({
      pathname: CATALOGUE_LIST_PATH,
      state: open
        ? {
            openCatalogueId: open.id,
            catalogue: open,
            openTab: "products",
          }
        : undefined,
    });
  };

  useEffect(() => {
    let cancelled = false;
    if (!catalogueId) {
      setError("Catalogue is missing from this address.");
      setLoading(false);
      return;
    }
    if (hasNamedCatalogueSeed(seeded, catalogueId)) {
      setCatalogue(seeded);
      setLoading(false);
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    void resolveCatalogue(catalogueId, seeded).then((result) => {
      if (cancelled) {
        return;
      }
      setLoading(false);
      if (result.error) {
        setCatalogue(null);
        setError(result.error);
        return;
      }
      setCatalogue(result.catalogue || null);
    });
    return () => {
      cancelled = true;
    };
    // Seeded catalogue comes from history.state on first paint only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogueId]);

  return (
    <div className="catalogue-manager-page catalogue-add-from-inventory-page">
      {loading ? (
        <p className="catalogue-add-from-inventory-lead">Loading catalogue…</p>
      ) : null}
      {error ? (
        <div className="catalogue-add-from-inventory-error">
          <p className="catalogue-add-from-inventory-lead">{error}</p>
          <Button
            className="catalogue-toolbar-btn catalogue-toolbar-btn-outline"
            type="button"
            onClick={() => history.push(CATALOGUE_LIST_PATH)}
          >
            Back to catalogues
          </Button>
        </div>
      ) : null}
      {catalogue && !error && isSystemManagedCatalogue(catalogue) ? (
        <div className="catalogue-add-from-inventory-error">
          <p className="catalogue-add-from-inventory-lead">
            In stock and out of stock catalogues update themselves. Open the catalogue to browse products.
          </p>
          <Button
            className="catalogue-toolbar-btn catalogue-toolbar-btn-outline"
            type="button"
            onClick={() => goToCatalogue(catalogue)}
          >
            Back to catalogue
          </Button>
        </div>
      ) : null}
      {catalogue && !error && !isSystemManagedCatalogue(catalogue) ? (
        <CatalogueAddFromInventory
          catalogue={catalogue}
          onBack={() => goToCatalogue(catalogue)}
          onAdded={() => goToCatalogue(catalogue)}
        />
      ) : null}
    </div>
  );
};

export default CatalogueAddFromInventoryPage;
